// ACE OS 2.0 — workspace & navigation config (UI-R1).
// Structure/IA per ACE_OS_2 artifacts; visual language stays ACE black/white/red.
import {
  LayoutDashboard, Users, CalendarClock, Compass, Network, LineChart,
  Home, Sparkles, MoreHorizontal,
} from 'lucide-react';

// Each workspace: id, label, display title, icon, and contextual sections.
// `link` on a section points at an existing (legacy) surface so nothing is lost pre-migration.
export const WORKSPACES = [
  {
    id: 'my-ace',
    label: 'My ACE',
    title: 'MY ACE',
    icon: LayoutDashboard,
    blurb: 'Role-aware attention queue, your work, and the business pulse.',
    sections: [
      { id: 'attention', label: 'Attention Queue', kind: 'ACTION', note: 'What needs an ACE decision now — pulled from tasks, risk, overdue confirmations.', link: '/command-center?tab=ops' },
      { id: 'my-work', label: 'My Work', kind: 'ACTION', note: 'Your follow-ups and assigned tasks (marketing_tasks engine).', link: '/command-center?tab=clients' },
      { id: 'pulse', label: 'Business Pulse', kind: 'ANALYTIC', note: 'Live KPIs derived from ops mirrors — never the mirror itself.', link: '/command-center?tab=ops' },
    ],
  },
  {
    id: 'clients',
    label: 'Clients',
    title: 'CLIENTS',
    icon: Users,
    blurb: 'One Client 360 relationship workspace. CL-* stays canonical.',
    sections: [
      { id: 'directory', label: 'Directory', kind: 'RECORD', note: 'Find & manage people and companies.', link: '/command-center?tab=clients' },
      { id: 'conversations', label: 'Conversations', kind: 'ACTION', note: 'Embedded threads (Quo/OpenPhone + Communications).', link: '/command-center?tab=quo' },
      { id: 'plans', label: 'Plans', kind: 'DECISION', note: 'Membership & relationship plans in client context.', link: '/membership' },
    ],
  },
  {
    id: 'services',
    label: 'Services',
    title: 'SERVICES',
    icon: CalendarClock,
    blurb: 'One service lifecycle: Lead → Quote → Booked → Today → Active → Recovery → Completed.',
    sections: [
      { id: 'leads', label: 'Leads', kind: 'ACTION', note: 'Inquiries & intake (INQ→QT chain preserved).', link: '/os/services?tab=leads' },
      { id: 'quotes', label: 'Quotes', kind: 'ACTION', note: 'Price & send. Pricing authority unchanged.', link: '/os/services?tab=quotes' },
      { id: 'booked', label: 'Booked', kind: 'RECORD', note: 'Confirmed work (canonical BK-*).', link: '/os/services?tab=booked' },
      { id: 'today', label: 'Today', kind: 'STATUS', note: "Today's operations.", link: '/command-center?tab=dailyops' },
      { id: 'active', label: 'Active', kind: 'STATUS', note: 'In-service + live tracking.', link: '/admin/tracking' },
      { id: 'recovery', label: 'Recovery', kind: 'ACTION', note: 'Service failures — first-class red state.', link: '/command-center?tab=opslogs' },
      { id: 'completed', label: 'Completed', kind: 'RECORD', note: 'History & ops notes.', link: '/command-center?tab=opslogs' },
    ],
  },
  {
    id: 'auto-lifestyle',
    label: 'Lifestyle',
    title: 'AUTO LIFESTYLE',
    icon: Compass,
    blurb: 'The differentiating ACE workspace: GPS, Profiles, Garage, Decisions, Consulting, Membership, Routes.',
    sections: [
      { id: 'gps', label: 'GPS', kind: 'DECISION', note: 'Destination navigation (ALJ/ALR).', link: '/auto-lifestyle' },
      { id: 'profiles', label: 'Profiles', kind: 'RECORD', note: 'Living Auto Lifestyle projection (ALP-*).', link: '/auto-lifestyle' },
      { id: 'garage', label: 'Garage', kind: 'RECORD', note: 'Client vehicle inventory (planned — unknown stays unknown).' },
      { id: 'decisions', label: 'Decisions', kind: 'DECISION', note: 'Ownership vs access decision cards.' },
      { id: 'consulting', label: 'Consulting', kind: 'ACTION', note: 'Human consultation workflow.', link: '/admin/consulting' },
      { id: 'membership', label: 'Membership', kind: 'DECISION', note: 'Relationship fit vs authoritative rules.', link: '/admin/loyalty' },
      { id: 'routes', label: 'Routes', kind: 'RECORD', note: 'Roadmaps & reroute history.', link: '/auto-lifestyle' },
    ],
  },
  {
    id: 'network',
    label: 'Network',
    title: 'NETWORK',
    icon: Network,
    blurb: 'One supply workspace: Fleet, Drivers, Partners, Fulfillment, Compliance.',
    sections: [
      { id: 'fleet', label: 'Fleet', kind: 'RECORD', note: 'Physical units, tiers, maintenance.', link: '/command-center?tab=teampay' },
      { id: 'drivers', label: 'Drivers', kind: 'RECORD', note: 'Availability & performance.', link: '/command-center?tab=teampay' },
      { id: 'partners', label: 'Partners', kind: 'RECORD', note: 'Affiliates & fleet owners (AFF-*/OWN-*).', link: '/admin/partners' },
      { id: 'fulfillment', label: 'Fulfillment', kind: 'ACTION', note: 'ACE Direct → Preferred → Collective → Special + Control.', link: '/command-center?tab=fulfillment' },
      { id: 'compliance', label: 'Compliance', kind: 'STATUS', note: 'Provider compliance & expiry impact.', link: '/command-center?tab=fulfillment' },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    title: 'BUSINESS',
    icon: LineChart,
    blurb: 'Money, Revenue, Payouts, Growth, Performance, Reports, Administration.',
    sections: [
      { id: 'money', label: 'Money', kind: 'RECORD', note: 'Ledger, payments, invoices, receivables.', link: '/command-center?tab=contracts' },
      { id: 'revenue', label: 'Revenue', kind: 'ANALYTIC', note: 'Weekly/monthly P&L.', link: '/command-center?tab=weeklypl' },
      { id: 'payouts', label: 'Payouts', kind: 'ACTION', note: 'Owner & partner payables (REC-* canonical).', link: '/command-center?tab=fulfillment' },
      { id: 'growth', label: 'Growth', kind: 'ANALYTIC', note: 'Marketing ROI & pipeline.', link: '/command-center?tab=ops' },
      { id: 'performance', label: 'Performance', kind: 'ANALYTIC', note: 'Executive control room.', link: '/admin/control-room' },
      { id: 'reports', label: 'Reports', kind: 'ANALYTIC', note: 'Exportable reporting.', link: '/command-center?tab=monthlypl' },
      { id: 'admin', label: 'Administration', kind: 'ACTION', note: 'Users, CMS, media, pricing admin.', link: '/admin/users' },
    ],
  },
];

export const WORKSPACE_BY_ID = Object.fromEntries(WORKSPACES.map((w) => [w.id, w]));

// Mobile bottom navigation: Home | Clients | Services | Lifestyle | More
export const MOBILE_NAV = [
  { id: 'my-ace', label: 'Home', icon: Home },
  { id: 'clients', label: 'Clients', icon: Users },
  { id: 'services', label: 'Services', icon: CalendarClock },
  { id: 'auto-lifestyle', label: 'Lifestyle', icon: Compass },
  { id: 'more', label: 'More', icon: MoreHorizontal },
];

// Quick Create actions route to existing authoritative flows (no new write path in R1).
export const QUICK_CREATE = [
  { id: 'qc-booking', label: 'New Booking', hint: 'Smart form · BK-*', to: '/os/services/new' },
  { id: 'qc-quote', label: 'New Quote', hint: 'Smart form · QT-*', to: '/os/services/quotes/new' },
  { id: 'qc-client', label: 'New Client', hint: 'CL-*', to: '/command-center?tab=clients' },
  { id: 'qc-gps', label: 'Start Auto Lifestyle GPS', hint: 'ALJ-*', to: '/auto-lifestyle' },
  { id: 'qc-fulfillment', label: 'Fulfillment Control', hint: 'REC-* / FRQ-*', to: '/command-center?tab=fulfillment' },
];

export const KIND_META = {
  ACTION: { label: 'Action', color: 'var(--os-red)' },
  STATUS: { label: 'Status', color: 'var(--os-info)' },
  RECORD: { label: 'Record', color: 'var(--os-muted)' },
  DECISION: { label: 'Decision', color: 'var(--os-warning)' },
  ANALYTIC: { label: 'Analytic', color: 'var(--os-success)' },
};

export const OS_ICONS = { Sparkles };
